import type { Layout } from '../../data/types';
import type { Vec2 } from '../../geo/types';
import { createSpatialHash } from '../../geo/spatialHash';
import { centroid, pointInRing } from '../../geo/polygon';
import type { LifeClearance } from './routes';
import { routeClear } from './routes';

export interface StreetNode {
  readonly position: Vec2;
  readonly streetId: string;
  readonly neighbours: number[];
}
export interface StreetGraph {
  readonly nodes: readonly StreetNode[];
  nearest(position: Vec2, maxDistance: number): number | null;
  path(from: number, to: number): readonly Vec2[] | null;
  /** How far along the streets every node within `limit` metres of `from` lies from it. */
  within(from: number, limit: number): ReadonlyMap<number, number>;
}

const SPACING = 5;
const RADIUS = 0.5;

/** Public streets and squares only. Every edge checks the full body against the city. */
export function buildStreetGraph(layout: Layout, clearance: LifeClearance): StreetGraph {
  const nodes: StreetNode[] = [];
  const index = createSpatialHash<number>(8);
  const nearby = (p: Vec2, reach: number) =>
    index.query({
      minX: p[0] - reach,
      maxX: p[0] + reach,
      minY: p[1] - reach,
      maxY: p[1] + reach,
    });
  const add = (position: Vec2, streetId: string) => {
    if (!clearance.clear(position, RADIUS + 0.25)) return;
    if (nearby(position, 0.5).some((i) => distance(position, nodes[i]!.position) < 0.5)) return;
    const i = nodes.length;
    nodes.push({ position, streetId, neighbours: [] });
    index.insert(i, { minX: position[0], maxX: position[0], minY: position[1], maxY: position[1] });
  };
  for (const street of layout.streets) {
    if (street.width < 2.1) continue;
    for (let i = 1; i < street.centerline.length; i++) {
      const a = street.centerline[i - 1]!,
        b = street.centerline[i]!;
      const n = Math.max(1, Math.ceil(distance(a, b) / SPACING));
      for (let j = 0; j <= n; j++)
        add([a[0] + ((b[0] - a[0]) * j) / n, a[1] + ((b[1] - a[1]) * j) / n], street.id);
    }
  }
  for (const square of layout.squares) {
    const centre = centroid(square.polygon);
    for (let y = -40; y <= 40; y += SPACING)
      for (let x = -40; x <= 40; x += SPACING) {
        const p: Vec2 = [centre[0] + x, centre[1] + y];
        if (pointInRing(p, square.polygon)) add(p, square.id);
      }
  }
  nodes.forEach((node, i) => {
    for (const j of nearby(node.position, SPACING * 1.6)) {
      if (j <= i || distance(node.position, nodes[j]!.position) > SPACING * 1.6) continue;
      if (!routeClear({ a: node.position, b: nodes[j]!.position, radius: RADIUS }, clearance))
        continue;
      node.neighbours.push(j);
      nodes[j]!.neighbours.push(i);
    }
  });
  return {
    nodes,
    nearest(position, maxDistance) {
      let best: number | null = null,
        gap = maxDistance;
      for (const i of nearby(position, maxDistance)) {
        const d = distance(position, nodes[i]!.position);
        if (d < gap) {
          best = i;
          gap = d;
        }
      }
      return best;
    },
    path(from, to) {
      return shortestPath(nodes, from, to);
    },
    within(from, limit) {
      const costs = new Map([[from, 0]]),
        open = new Set([from]);
      while (open.size) {
        let current = -1;
        for (const i of open) if (current < 0 || costs.get(i)! < costs.get(current)!) current = i;
        open.delete(current);
        for (const next of nodes[current]!.neighbours) {
          const cost =
            costs.get(current)! + distance(nodes[current]!.position, nodes[next]!.position);
          if (cost > limit || cost >= (costs.get(next) ?? Infinity)) continue;
          costs.set(next, cost);
          open.add(next);
        }
      }
      return costs;
    },
  };
}

export const distance = (a: Vec2, b: Vec2): number => Math.hypot(b[0] - a[0], b[1] - a[1]);

function shortestPath(
  nodes: readonly StreetNode[],
  from: number,
  to: number
): readonly Vec2[] | null {
  if (!nodes[from] || !nodes[to]) return null;
  const costs = new Map([[from, 0]]),
    parents = new Map<number, number>();
  const open = new Set([from]);
  while (open.size) {
    let current = -1,
      score = Infinity;
    for (const i of open) {
      const candidate = costs.get(i)! + distance(nodes[i]!.position, nodes[to].position);
      if (candidate < score) {
        current = i;
        score = candidate;
      }
    }
    if (current === to) {
      const path = [nodes[to].position];
      while (current !== from) {
        current = parents.get(current)!;
        path.push(nodes[current]!.position);
      }
      return path.reverse();
    }
    open.delete(current);
    for (const next of nodes[current]!.neighbours) {
      const cost = costs.get(current)! + distance(nodes[current]!.position, nodes[next]!.position);
      if (cost >= (costs.get(next) ?? Infinity)) continue;
      costs.set(next, cost);
      parents.set(next, current);
      open.add(next);
    }
  }
  return null;
}
